import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormControl } from "@angular/forms";
import { Subscription } from "rxjs";
import { tap } from "rxjs/operators";
//import { GridOptions, AllModules, ColDef } from "@ag-grid-enterprise/all-modules";
import { GridOptions, ColDef, GridApi } from "ag-grid-community";

import { BaseControlComponent } from "../base-control/base-control.component";
import { ContextInteractionService } from "../../services/context-interaction.service";
import { DocumentManagementService, IUploadFile } from "../../../common/services/document-management.service";
import { ParserPropertyModel, ParseFileResponse, ParserError } from "../../../builder/components/state/controls/config/data/file-parser-config/parser.model";
import { LoaderService } from "../../../common/services/loader.service";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { DisplayService } from "../../../common/services/display.service";
import { UploadViewFilesComponent } from "../../../common/components/fileUpload/upload-view-files.component";


@Component({
    selector: 'app-file-parser',
    templateUrl: './file-parser.component.html',
    styleUrls: ['./file-parser.component.scss', '../file-upload/file-upload.component.scss'],
    standalone: false
})
export class FileParserComponent extends BaseControlComponent implements OnInit, OnDestroy {
    static componentName = "FileParserComponent";

    @ViewChild("downloadLink") downloadLink: ElementRef;
    @ViewChild(UploadViewFilesComponent) filesComponent: UploadViewFilesComponent;
    csvConfirmationMessage: string = "Your request has been completed.";
    excelConfirmationMessage: string = "Your request has been completed. <br /> The Excel formatted sample uses the following formatting: <br />  Required fields are in <span class=\"bold red\">red</span>. <br /> API Number fields are in <span style=\"color: #1f6581; font-weight: bold;\">blue</span>. <br /> Required API Number fields are in <span style=\"color: #800080; font-weight: bold;\">purple</span>.  <br /> Optional fields have no styling.";

   
    get dataModel(): ParserPropertyModel[] {
        if (!this.componentData || !this.componentData.config || !this.componentData.config.parser) return null;
        return <ParserPropertyModel[]>this.componentData.config.parser;
    }

    private _dataColumns: ColDef[];
    
    get dataColumns(): ColDef[] {
        if (this._dataColumns == null) {
            this._dataColumns = [];
            if (this.dataModel != null) {
                for (let p = 0; p < this.dataModel.length; p++) {
                    const headerName = this.formatHeaderName(this.dataModel[p].propertyName);
                    let column = {
                        headerName: headerName,
                        headerTooltip: headerName,
                        field: this.dataModel[p].propertyName,
                        tooltipField: this.dataModel[p].propertyName,
                        valueGetter: this.getPropertyValue(this.dataModel[p].propertyName),
                        tooltipValueGetter: this.getPropertyValue(this.dataModel[p].propertyName),
                        suppressMovable: true
                    } as ColDef;
                    this._dataColumns.push(column);
                }
            }
        }      
        return this._dataColumns;
    }


    get disable(): boolean {
        return this.isEdit || !this.isEnabled;
    }

    get hasFileListing(): boolean {
        return this.uploadedFiles != null && this.uploadedFiles.length > 0;
	}

	get headerKey(): string {
		return `${this.controlId}_header`;
	}

    fileRead: boolean = false;

    get errorData(): ParserError[] {
        let value = this.getContext("errors");
        if (value == null || !Array.isArray(value)) {
            value = [];
            this.storeContext(value, "errors");
        }
        return value;
    }

    set errorData(value: ParserError[]) {
        this.storeContext(value, "errors");
	}

	get hasHeaders(): boolean {
		var value = <boolean>this.getContext("headers"); 
		if (value == null) value = false;
		return value; 
	}

	set hasHeaders(value: boolean) {
		this.storeContext(value, "headers");
	}

    get parsedData(): any {
        return this.getContextValue();
    }

    set parsedData(value: any) {
        this.setContextValue(value);
    }

    get errorGridColumns(): ColDef[] {
        return [
            {
                headerName: "Line",
                headerTooltip: "Line",
                field: "line",
                tooltipField: "line",
                suppressMovable: true,
                minWidth: 50
            } as ColDef,
            {
                headerName: "Column",
                headerTooltip: "Column",
                field: "column",
                tooltipField: "column",
                suppressMovable: true,
                minWidth: 50
            } as ColDef,
            {
                headerName: "Error",
                headerTooltip: "Error",
                field: "error",
                tooltipField: "error",
                suppressMovable: true
            } as ColDef
        ];
    }

	errorGridOptions: GridOptions;
	errorGridApi?: GridApi;
	parsedGridOptions: GridOptions;
	parsedGridApi?: GridApi;

    sampleRequestCompleteDialog = new Dialog("Your request has been completed.", [new DialogButton("OK", false, () => { return; })]);
    sampleDialog: Dialog = new Dialog("Please choose a sample format.", [new DialogButton("CSV", true, () => { this.getSample("csv"); }), new DialogButton("Excel", true, () => { this.getSample("xlsx"); }), new DialogButton("Cancel", false, () => { return; })]);

    subscription: Subscription = new Subscription();

    get uploadedFiles(): IUploadFile[] {
        let value = this.getContext("files");
        if (value == null || !Array.isArray(value)) {
            value = [];
            this.storeContext(value, "files");
        }
        return value;
    }


    constructor(svc: ContextInteractionService, private _docService: DocumentManagementService, private _loader: LoaderService, private _dialog: DynamicDialogService,
            private _formatter: GridFormatterService, private _displayService: DisplayService) {
        super(svc);
    }

    ngOnInit() {
		super.ngOnInit();
		this.formGroup.addControl("hasHeaders", new FormControl(this.hasHeaders));
        this.setupGrids();
        if (this.uploadedFiles != null && this.uploadedFiles.length > 0) {
            this.fileRead = true;            
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.subscription) this.subscription.unsubscribe();
    }

    deleteFile(file: IUploadFile): void {
        let index = this.uploadedFiles.findIndex(f => f.rawFile === file.rawFile);
        if (index !== -1) {
            this.uploadedFiles.splice(index, 1);            
            this.errorData = [];
            this.parsedData = [];
            this.filesComponent.removeFile(file);
        }
        this.fileRead = this.uploadedFiles != null && this.uploadedFiles.length > 0;       
    }

    formatHeaderName(name: string): string {
        const parts = name.split(".");
        let formatted: string = "";
        for (let i = 0; i < parts.length; i++) {
            let result = this._displayService.splitEnum(parts[i]);
            if (i > 0) formatted += ' ';
            formatted += `${result}`;
        }
        return formatted;
    }

    getPropertyValue(field: string): any {
        return (obj) => {
            if (obj.data) {
                let fieldValue = obj.data[field];
                if (fieldValue == null) return "";
                return fieldValue;
            }
        }
    }

    getSample(type: string): void {
        if (this.dataModel != null) {
            this._loader.toggleLoader(true);
            const sample$ = this._docService.generateSampleParserFile(type, this.dataModel).pipe()
                .subscribe(
                    (data: any) => {
                        this._loader.toggleLoader(false);
                        const extension = (type.toLocaleLowerCase() === "csv") ? "csv" : "xlsx";
                        const fileName = `sample.${extension}`;
                        const fileType = (type.toLocaleLowerCase() === "csv") ? "text/csv" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
                        const blob = new Blob([data], { type: fileType });
						const blobUrl = window.URL.createObjectURL(blob);
						const anchor = <HTMLAnchorElement>this.downloadLink.nativeElement;
						anchor.href = blobUrl;
						anchor.download = fileName;
						anchor.click();
                        this.showDownloadConfirmation(type);
                    },
                    (error) => {
                        this.handleError(error);
                    }
            );
            this.subscription.add(sample$);
        }
    }

    handleError(error: any): void {
        console.error(error);
        this.fileRead = true;
        this._loader.toggleLoader(false);
        this._dialog.showError();
    }

    parseFile(file: IUploadFile): void {
        if (file != null) {
            this.uploadedFiles.push(file);
            this.fileRead = false;
            this._loader.toggleLoader(true);
            const parse$ = this._docService.parseFile(file, this.dataModel, this.hasHeaders).pipe(
                tap((data: ParseFileResponse) => {
                    this.parsedData = data.data;
                    this.errorData = data.errors;
                })
            ).subscribe(
                (data: any) => {
                    this.fileRead = true;
                    this._loader.toggleLoader(false);
                    if (this.parsedGridApi) this.parsedGridApi.sizeColumnsToFit();
                    if (this.errorGridApi) this.errorGridApi.sizeColumnsToFit();
                },
                (error) => {
                    this.handleError(error);
                }
            );
            this.subscription.add(parse$);
        }        
    }


    requestSample(): void {
        this.sampleDialog.toggleDialog(true);
        this._dialog.updateDialog(this.sampleDialog);
    }

    setupGrids(): void {
		this.errorGridOptions = this._formatter.BuildDefaultGridOptionsWithColumns(this.errorGridColumns, false, true, true, true);
		this.errorGridOptions.onGridReady = (params) => { this.errorGridApi = params.api; };
		this.parsedGridOptions = this._formatter.BuildDefaultGridOptionsWithColumns(this.dataColumns, false, true, true, true);    
		this.parsedGridOptions.onGridReady = (params) => { this.parsedGridApi = params.api; };
	}

	setHasHeaders(evt: any): void {
		console.log('setHasHeaders event', evt);
		this.hasHeaders = evt;
	}

    showDownloadConfirmation(fileType: string): void {
        this.sampleRequestCompleteDialog.message = (fileType.toLocaleLowerCase() === "csv") ? this.csvConfirmationMessage : this.excelConfirmationMessage;
        this.sampleRequestCompleteDialog.toggleDialog(true);
        this._dialog.updateDialog(this.sampleRequestCompleteDialog);
    }
}
